import type { ReactNode } from 'react'
import { EyeOffIcon } from '@/components/Icons'

/**
 * The bar at the top of a live-meeting column: an icon, a name, whatever
 * belongs on the right, and the control that folds the column away.
 *
 * Shared rather than copied. Every column in the live screen wears this, and
 * Copilot used to wear its own near-miss of it — a different height, a
 * different background, hand-written pixel padding — which is the kind of
 * difference that only shows up side by side (BRAI-681).
 *
 * @param title Plain text is surfaced as a `title` attribute too, so the full
 *   name stays discoverable on hover when a narrow column truncates it.
 * @param actions Anything to sit left of the fold control, such as a status.
 * @param onHide Omit to leave the column unfoldable, and the control out.
 */
export default function PanelHeader({
  icon,
  title,
  actions,
  className,
  onHide,
  hideLabel,
  hideDisabled = false,
}: {
  icon?: ReactNode
  title: ReactNode
  actions?: ReactNode
  className?: string
  onHide?: () => void
  hideLabel?: string
  hideDisabled?: boolean
}) {
  // When the title is plain text we surface it as an HTML tooltip so the full
  // panel name stays discoverable on hover even when it truncates in a narrow
  // pane.
  const tooltip = typeof title === 'string' ? title : undefined
  return (
    <div
      className={[
        'flex shrink-0 items-center justify-between gap-2 border-b border-blue-100 bg-blue-50/40 px-3 py-2 dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)] sm:gap-3 sm:px-4 sm:py-3',
        className ?? '',
      ].join(' ')}
    >
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        {icon ? (
          <span className="shrink-0 text-blue-600 dark:text-[var(--accent)] [&_svg]:h-4 [&_svg]:w-4 sm:[&_svg]:h-5 sm:[&_svg]:w-5">
            {icon}
          </span>
        ) : null}
        <p
          title={tooltip}
          className="min-w-0 truncate text-xs font-bold text-slate-900 dark:text-[var(--text-primary)] sm:text-sm"
        >
          {title}
        </p>
      </div>
      {actions || onHide ? (
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          {onHide ? (
            <button
              type="button"
              onClick={onHide}
              disabled={hideDisabled}
              aria-disabled={hideDisabled}
              aria-label={hideLabel}
              title={hideLabel}
              className={[
                'flex h-7 w-7 items-center justify-center rounded-md text-blue-600 transition-colors dark:text-[var(--accent)]',
                hideDisabled
                  ? 'cursor-not-allowed opacity-40'
                  : 'hover:bg-blue-100 hover:text-blue-700 dark:hover:bg-[var(--accent-light)] dark:hover:text-[var(--accent-hover)]',
              ].join(' ')}
            >
              <EyeOffIcon className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
